import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { SEO } from '../../utils/seo';

export default function SpeakersPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [hoveredSpeaker, setHoveredSpeaker] = useState<number | null>(null);

  const categories = ['All', 'Investors', 'Family Office', 'Founders', 'Culture'];

  const speakers = [
    {
      name: 'Layla Al-Rashid',
      title: 'Managing Partner',
      role: 'Family Office Principal',
      region: 'Dubai & Riyadh',
      category: 'Family Office',
      quote: 'Designing cross border structures for multi generational families between Europe and the Gulf.',
      image: 'https://public.readdy.ai/ai/img_res/b6386f76c581e621ca36f2dfee96fb46.jpg',
    },
    {
      name: 'Sophie Beaumont',
      title: 'Founder & CEO',
      role: 'Private Equity Investor',
      region: 'Paris & Geneva',
      category: 'Investors',
      quote: 'Building bridges between traditional capital and emerging markets with precision and cultural intelligence.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20elegant%20European%20businesswoman%20in%20sophisticated%20setting%20confident%20leadership%20presence%20refined%20aesthetic%20editorial%20photography%20warm%20neutral%20tones%20executive%20style%20natural%20lighting&width=600&height=750&seq=speaker-new-002&orientation=portrait',
    },
    {
      name: 'Amira Hassan',
      title: 'Principal',
      role: 'Venture Capital & Tech',
      region: 'London & Beirut',
      category: 'Investors',
      quote: 'Championing next generation founders who are reshaping industries with innovation and purpose.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20elegant%20businesswoman%20in%20sophisticated%20setting%20confident%20presence%20refined%20aesthetic%20editorial%20photography%20warm%20neutral%20tones%20professional%20executive%20style%20natural%20lighting&width=600&height=750&seq=speaker-new-003&orientation=portrait',
    },
    {
      name: 'Isabella Rossi',
      title: 'Director',
      role: 'Art & Cultural Philanthropy',
      region: 'Milan & Dubai',
      category: 'Culture',
      quote: 'Curating collections and cultural initiatives that transcend borders and create lasting legacies.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20elegant%20businesswoman%20in%20sophisticated%20setting%20confident%20presence%20refined%20aesthetic%20editorial%20photography%20warm%20neutral%20tones%20luxury%20professional%20environment%20natural%20lighting&width=600&height=750&seq=speaker-new-004&orientation=portrait',
    },
    {
      name: 'Noor Al-Mansouri',
      title: 'Next Gen Leader',
      role: 'Family Office Succession',
      region: 'Abu Dhabi & New York',
      category: 'Family Office',
      quote: 'Redefining family governance for the next generation with clarity, courage, and modern frameworks.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20elegant%20young%20businesswoman%20in%20sophisticated%20setting%20confident%20presence%20refined%20aesthetic%20editorial%20photography%20warm%20neutral%20tones%20executive%20style%20natural%20lighting&width=600&height=750&seq=speaker-new-005&orientation=portrait',
    },
    {
      name: 'Catherine Dubois',
      title: 'Senior Advisor',
      role: 'Wealth Strategy & Governance',
      region: 'Geneva & Paris',
      category: 'Founders',
      quote: 'Architecting wealth structures that honor tradition while embracing innovation and global complexity.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20elegant%20businesswoman%20in%20sophisticated%20setting%20confident%20executive%20presence%20refined%20aesthetic%20editorial%20photography%20warm%20neutral%20tones%20professional%20style%20natural%20lighting&width=600&height=750&seq=speaker-new-006&orientation=portrait',
    },
  ];

  const filteredSpeakers = selectedCategory === 'All' 
    ? speakers 
    : speakers.filter(s => s.category === selectedCategory);
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Summit Speakers",
    "description": "Meet the visionary leaders speaking at the Women In Wealth Summit. Investors, family office principals, and cultural figures from across the globe.",
    "about": {
      "@type": "Event",
      "name": "Women In Wealth Summit",
      "performer": speakers.map(speaker => ({
        "@type": "Person",
        "name": speaker.name,
        "jobTitle": speaker.title,
        "description": speaker.quote
      }))
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Summit Speakers | Women In Wealth · Beirut 2026"
        description="Meet the visionary leaders speaking at the Women In Wealth Summit in Beirut. Featuring investors, family office principals, venture capitalists, and cultural figures from Paris, Dubai, Riyadh, Geneva, London, and beyond."
        keywords="women in wealth speakers, summit speakers, women investors speakers, family office speakers, venture capital women, private equity speakers, wealth management speakers"
        canonical={`${siteUrl}/speakers`}
        schema={schema}
      />
      <Navigation />

      {/* Hero - Split Design */}
      <section className="min-h-[70vh] grid grid-cols-1 lg:grid-cols-2">
        {/* Left - Content */}
        <div className="flex items-center px-8 lg:px-16 xl:px-24 py-32 bg-slate-900 order-2 lg:order-1">
          <div>
            <p className="text-champagne-400 text-sm uppercase tracking-[0.4em] mb-6 font-medium">
              Curated Voices
            </p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-white leading-[1.05] mb-8">
              Visionary Leaders
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed mb-8 max-w-lg">
              Hosted by Racha Mezher, the summit brings together investors, family office leaders, founders, and cultural figures who embody clarity, courage, and global influence.
            </p>
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/5 border border-white/10 rounded-full">
              <i className="ri-double-quotes-l text-champagne-400"></i>
              <span className="text-slate-300 italic">More than speakers — co architects of the room.</span>
            </div>
          </div>
        </div>

        {/* Right - Image Collage */}
        <div className="relative h-[400px] lg:h-auto order-1 lg:order-2 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20sophisticated%20women%20professionals%20in%20luxury%20conference%20setting%20Mediterranean%20architecture%20golden%20hour%20lighting%20refined%20aesthetic%20editorial%20style%20warm%20tones%20professional%20atmosphere%20high%20end%20environment%20collaborative%20discussion&width=1000&height=800&seq=speakers-hero-001&orientation=landscape"
            alt="Summit Speakers"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-slate-900/30 lg:hidden"></div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-20 z-40 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="px-8 lg:px-16 xl:px-24">
          <div className="max-w-7xl mx-auto py-4">
            <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide">
              <span className="text-sm text-slate-500 flex-shrink-0 hidden sm:block">Filter:</span>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    selectedCategory === category
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Speakers Grid - Asymmetric Masonry */}
      <section className="py-20 px-8 lg:px-16 xl:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredSpeakers.map((speaker, index) => (
              <div
                key={index}
                onMouseEnter={() => setHoveredSpeaker(index)}
                onMouseLeave={() => setHoveredSpeaker(null)}
                className={`group cursor-pointer ${
                  index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''
                }`}
              >
                {/* Featured Speaker (First) */}
                {index === 0 ? (
                  <div className="relative h-full min-h-[600px] rounded-3xl overflow-hidden">
                    <img
                      src={speaker.image}
                      alt={speaker.name}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent"></div>
                    
                    {/* Content Overlay */}
                    <div className="absolute inset-0 p-8 lg:p-12 flex flex-col justify-end">
                      <div className="mb-4">
                        <span className="inline-flex px-4 py-1.5 bg-champagne-500/20 backdrop-blur-sm rounded-full text-champagne-300 text-xs font-medium">
                          {speaker.category}
                        </span>
                      </div>
                      <h3 className="text-3xl lg:text-4xl font-display font-bold text-white mb-2">
                        {speaker.name}
                      </h3>
                      <p className="text-champagne-300 font-medium mb-1">{speaker.title}</p>
                      <p className="text-slate-400 text-sm mb-4">{speaker.role} · {speaker.region}</p>
                      <p className="text-slate-300 leading-relaxed max-w-lg italic">
                        "{speaker.quote}"
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Regular Speaker Cards */
                  <div className="relative rounded-2xl overflow-hidden bg-white border border-slate-100 hover:border-slate-200 hover:shadow-2xl transition-all duration-500">
                    {/* Image */}
                    <div className="relative h-[320px] overflow-hidden">
                      <img
                        src={speaker.image}
                        alt={speaker.name}
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className={`absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent transition-opacity duration-500 ${
                        hoveredSpeaker === index ? 'opacity-100' : 'opacity-60'
                      }`}></div>

                      {/* Hover Quote */}
                      <div className={`absolute inset-0 p-6 flex items-end transition-all duration-500 ${
                        hoveredSpeaker === index ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                      }`}>
                        <p className="text-white text-sm leading-relaxed italic">
                          "{speaker.quote}"
                        </p>
                      </div>

                      {/* Category Badge */}
                      <div className="absolute top-4 left-4">
                        <span className="inline-flex px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-slate-700 text-xs font-medium">
                          {speaker.category}
                        </span>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-6">
                      <h3 className="text-xl font-display font-bold text-slate-900 mb-1 group-hover:text-champagne-700 transition-colors duration-300">
                        {speaker.name}
                      </h3>
                      <p className="text-sm text-slate-600 mb-1">{speaker.title} · {speaker.role}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <i className="ri-map-pin-line text-champagne-500"></i>
                        <span>{speaker.region}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Host Feature */}
      <section className="py-24 px-8 lg:px-16 xl:px-24 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Image */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-champagne-200 to-champagne-100 rounded-3xl opacity-30 blur-2xl"></div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20elegant%20businesswoman%20in%20sophisticated%20setting%20confident%20leadership%20presence%20refined%20aesthetic%20editorial%20photography%20warm%20neutral%20tones%20executive%20style%20luxury%20professional%20environment%20natural%20lighting&width=600&height=700&seq=host-speakers-001&orientation=portrait"
                    alt="Racha Mezher"
                    className="w-full h-[500px] object-cover object-top"
                  />
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-7 lg:pl-8">
              <p className="text-champagne-600 text-sm uppercase tracking-[0.3em] mb-4 font-medium">
                Your Host
              </p>
              <h2 className="text-4xl lg:text-5xl font-display font-bold text-slate-900 mb-2">
                Racha Mezher
              </h2>
              <p className="text-lg text-slate-500 mb-8">
                Founder & CEO, Wealthrise Management
              </p>
              
              <div className="w-16 h-px bg-champagne-400 mb-8"></div>
              
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                Racha personally curates every aspect of this summit, ensuring that each participant, conversation, and experience reflects the values of discretion, excellence, and meaningful connection.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                Her global footprint across Paris, Dubai, Riyadh, Geneva, Beirut, and London brings a unique cross-cultural perspective to wealth management and family governance.
              </p>

              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-champagne-600 font-medium hover:text-champagne-700 transition-colors duration-300 cursor-pointer group"
              >
                Learn more about the vision
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-8 lg:px-16 xl:px-24 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6">
            Join These Visionary Leaders
          </h2>
          <p className="text-lg text-slate-400 mb-8">
            A curated room, by application only.
          </p>
          <Link
            to="/attend"
            className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-champagne-500 text-slate-900 rounded-full font-medium hover:bg-champagne-400 transition-all duration-300 cursor-pointer whitespace-nowrap group"
          >
            Request an Invitation
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
